import { Component, EventEmitter, OnDestroy, Output, ViewEncapsulation, inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import { Router, RouterModule, NavigationEnd } from "@angular/router";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { MatTooltipModule } from "@angular/material/tooltip";
import { Subscription, filter, startWith } from "rxjs";
import { AuthService } from "@services/auth.service";
import { TranslateModule } from "@ngx-translate/core";

interface SidebarMenuChild {
  labelKey: string;
  url: string;
  imgIcon?: string;
  imgIconActive?: string;
}

interface SidebarMenuItem {
  labelKey: string;
  icon: string;
  imgIcon?: string;
  imgIconActive?: string;
  url?: string;
  children?: SidebarMenuChild[];
  show: boolean;
  adminOnly?: boolean;
  isActive?: (path: string) => boolean;
}

@Component({
  selector: "kzn-sidebar-create",
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    TranslateModule,
  ],
  templateUrl: "./sidebar-create.component.html",
  styleUrl: "./sidebar-create.component.scss",
  encapsulation: ViewEncapsulation.None,
})
export class SidebarCreateComponent implements OnDestroy {
  @Output() menuItemClick = new EventEmitter<void>();

  expanded = true;
  currentRoute = "";

  private _router = inject(Router);
  private _authService = inject(AuthService);
  private _routerSub: Subscription;
  private _userDetailsSub?: Subscription;

  isAdminUser = false;

  menus: SidebarMenuItem[] = [
    {
      labelKey: "navCustomers",
      icon: "supervisor_account",
      url: "/customers",
      show: true,
      adminOnly: true,
      isActive: (path) => path === "/customers" || path.startsWith("/customers/"),
    },
    {
      labelKey: "navMyRequest",
      icon: "",
      imgIcon: "assets/request_side_icon.png",
      imgIconActive: "assets/request-active.svg",
      url: "/requests",
      show: true,
    },
    {
      labelKey: "navSignRequestGroup",
      icon: "",
      show: true,
      children: [
        {
          labelKey: "navCreateSignRequest",
          url: "/create/new",
          imgIcon: "assets/create-request_icon.png",
          imgIconActive: "assets/crr-active.svg",
        },
        { labelKey: "navBulkSignRequest", url: "/create/bulk" },
      ],
    },
    {
      labelKey: "navEmailTemplates",
      icon: "",
      imgIcon: "assets/email-template-icon.png",
      imgIconActive: "assets/email-template-icon-active.svg",
      url: "/email-templates",
      show: true,
    },
    {
      labelKey: "navSettings",
      icon: "",
      imgIcon: "assets/setting_icon.png",
      imgIconActive: "assets/setting-active.svg",
      url: "/settings",
      show: true,
    },
    {
      labelKey: "navSupport",
      icon: "",
      imgIcon: "assets/support-icon.png",
      imgIconActive: "assets/support-icon-active.svg",
      url: "/support",
      show: true,
    },
  ];

  constructor() {
    this._routerSub = this._router.events
      .pipe(filter((e) => e instanceof NavigationEnd), startWith(this._router))
      .subscribe(() => {
        this.currentRoute = this._router.url;
      });

    this._userDetailsSub = this._authService.userDetails$.subscribe((user) => {
      this.isAdminUser = !!user && !user.CustomerId;
    });
  }

  childMenuIcon(url: string): string {
    return url === "/create/bulk" ? "groups" : "edit_document";
  }

  private pathOnly(url: string): string {
    const base = (url || "").split("?")[0].split("#")[0];
    return base.length > 1 && base.endsWith("/") ? base.slice(0, -1) : base;
  }

  isChildRouteActive(url: string): boolean {
    const current = this.pathOnly(this.currentRoute);
    const target = this.pathOnly(url);
    return current === target;
  }

  isLeafMenuActive(item: SidebarMenuItem | string | undefined): boolean {
    if (!item) {
      return false;
    }
    const menu = typeof item === "string" ? ({ url: item } as SidebarMenuItem) : item;
    if (!menu.url) {
      return false;
    }
    const current = this.pathOnly(this.currentRoute);
    if (typeof menu.isActive === "function") {
      return menu.isActive(current);
    }
    return current === this.pathOnly(menu.url);
  }

  onMenuItemClick(): void {
    this.menuItemClick.emit();
  }

  toggle(): void {
    this.expanded = !this.expanded;
  }

  navigateToCreate(): void {
    this._router.navigate(["/requests"]);
  }

  onSignRequestChildClick(event: MouseEvent, url: string): void {
    const path = this.pathOnly(this._router.url);
    if (path !== url) {
      return;
    }
    event.preventDefault();
    void this._router.navigateByUrl("/requests").then(() => {
      void this._router.navigateByUrl(url);
    });
  }

  ngOnDestroy(): void {
    this._routerSub?.unsubscribe();
    this._userDetailsSub?.unsubscribe();
  }
}
